import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import ConfirmModal from './components/ConfirmModal';

interface AdminUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  adeli_number: string | null;
  siret: string | null;
  subscription_status: string | null;
  subscription_end: string | null;
  analyses_this_month: number | null;
  role: string | null;
  created_at: string | null;
  email: string | null;
}

interface ConfirmState {
  user: AdminUser;
  status: 'active' | 'suspended';
}

interface ToastState {
  type: 'success' | 'error';
  message: string;
}

export default function AdminPage() {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.rpc('list_all_profiles');
      if (err) {
        setError(err.message);
        setUsers([]);
      } else {
        setUsers((data as AdminUser[]) ?? []);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const requestAction = (target: AdminUser, status: 'active' | 'suspended') => {
    setConfirm({ user: target, status });
  };

  const confirmAction = async () => {
    if (!confirm) return;
    setActionLoadingId(confirm.user.id);
    try {
      const { error: err } = await supabase.rpc('set_subscription_status', {
        p_target_id: confirm.user.id,
        p_status: confirm.status,
      });
      if (err) {
        setToast({ type: 'error', message: err.message });
      } else {
        setToast({
          type: 'success',
          message:
            confirm.status === 'active'
              ? 'Abonnement réactivé avec succès.'
              : 'Compte suspendu avec succès.',
        });
        await fetchUsers();
      }
    } catch (e) {
      setToast({
        type: 'error',
        message: e instanceof Error ? e.message : 'Erreur inconnue',
      });
    } finally {
      setActionLoadingId(null);
      setConfirm(null);
    }
  };

  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.subscription_status === 'active').length;
  const suspendedUsers = users.filter((u) => u.subscription_status === 'suspended').length;
  const adminUsers = users.filter((u) => u.role === 'admin').length;
  const totalAnalyses = users.reduce((sum, u) => sum + (u.analyses_this_month ?? 0), 0);

  const formatDate = (value: string | null) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const displayName = (u: AdminUser) => {
    const full = [u.first_name, u.last_name].filter(Boolean).join(' ');
    return full || '—';
  };

  const statusBadge = (u: AdminUser) => {
    if (u.subscription_status === 'active') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
          <i className="ri-checkbox-circle-line"></i>
          Actif
        </span>
      );
    }
    if (u.subscription_status === 'suspended') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
          <i className="ri-pause-circle-line"></i>
          Suspendu
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-200 text-foreground-500 text-xs font-medium">
        <i className="ri-time-line"></i>
        Inactif
      </span>
    );
  };

  const isActionable = (u: AdminUser) =>
    u.role !== 'admin' && u.id !== user?.id;

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-xl font-bold font-heading text-primary-600">
                CotIdel<span className="text-accent-500">.</span>
              </Link>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-semibold uppercase tracking-wide">
                <i className="ri-shield-star-line"></i>
                Administration
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/dashboard"
                className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                <i className="ri-dashboard-line"></i>
                <span className="hidden sm:inline">Tableau de bord</span>
              </Link>
              <button
                onClick={handleLogout}
                className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                <i className="ri-logout-box-r-line"></i>
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-1">
            Espace d&apos;administration
          </h1>
          <p className="text-sm text-foreground-500">
            Vue d&apos;ensemble des comptes IDEL et de l&apos;activité de la plateforme.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-team-line text-primary-600"></i>
              </div>
              <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">Comptes</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-foreground-950">{totalUsers}</p>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-vip-crown-line text-secondary-600"></i>
              </div>
              <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">Actifs</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-foreground-950">{activeUsers}</p>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-shield-star-line text-accent-600"></i>
              </div>
              <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">Admins</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-foreground-950">{adminUsers}</p>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-pause-circle-line text-red-600"></i>
              </div>
              <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">Suspendus</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-foreground-950">{suspendedUsers}</p>
          </div>
        </div>

        {/* User list */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-background-200/70">
            <h2 className="text-base font-bold font-heading text-foreground-950">
              Comptes utilisateurs
            </h2>
            <button
              onClick={fetchUsers}
              className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              Actualiser
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-xl mr-2"></i>
              <span className="text-sm">Chargement des comptes...</span>
            </div>
          ) : error ? (
            <div className="px-4 md:px-6 py-10 text-center">
              <div className="inline-flex items-center gap-2 text-red-600 text-sm mb-4">
                <i className="ri-error-warning-line text-lg"></i>
                <span>{error}</span>
              </div>
              <button
                onClick={fetchUsers}
                className="bg-primary-500 hover:bg-primary-600 text-white font-semibold px-5 py-2.5 rounded-md text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : users.length === 0 ? (
            <div className="px-4 md:px-6 py-16 text-center text-foreground-500">
              <i className="ri-inbox-line text-3xl block mb-3"></i>
              <p className="text-sm">Aucun compte pour le moment.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    <th className="px-4 md:px-6 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Utilisateur</th>
                    <th className="px-4 md:px-6 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">ADELI</th>
                    <th className="px-4 md:px-6 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Abonnement</th>
                    <th className="px-4 md:px-6 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Rôle</th>
                    <th className="px-4 md:px-6 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Analyses</th>
                    <th className="px-4 md:px-6 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Inscription</th>
                    <th className="px-4 md:px-6 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-background-200/50 hover:bg-background-100/40 transition-colors">
                      <td className="px-4 md:px-6 py-3">
                        <div className="text-sm font-medium text-foreground-950">{displayName(u)}</div>
                        <div className="text-xs text-foreground-400">{u.email || '—'}</div>
                      </td>
                      <td className="px-4 md:px-6 py-3 text-sm text-foreground-700 whitespace-nowrap">
                        {u.adeli_number || '—'}
                      </td>
                      <td className="px-4 md:px-6 py-3 whitespace-nowrap">
                        {statusBadge(u)}
                        {u.subscription_end && (
                          <div className="text-[11px] text-foreground-400 mt-1">
                            {u.subscription_status === 'active' ? 'Expire le ' : ''}
                            {formatDate(u.subscription_end)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 md:px-6 py-3 whitespace-nowrap">
                        {u.role === 'admin' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                            <i className="ri-shield-star-line"></i>
                            Admin
                          </span>
                        ) : (
                          <span className="text-xs text-foreground-500">IDEL</span>
                        )}
                      </td>
                      <td className="px-4 md:px-6 py-3 text-sm text-foreground-700 whitespace-nowrap">
                        {u.analyses_this_month ?? 0}
                      </td>
                      <td className="px-4 md:px-6 py-3 text-sm text-foreground-500 whitespace-nowrap">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="px-4 md:px-6 py-3 whitespace-nowrap">
                        {!isActionable(u) ? (
                          <span className="text-xs text-foreground-300">—</span>
                        ) : u.subscription_status === 'active' ? (
                          <button
                            onClick={() => requestAction(u, 'suspended')}
                            disabled={actionLoadingId === u.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoadingId === u.id ? (
                              <i className="ri-loader-4-line animate-spin"></i>
                            ) : (
                              <i className="ri-pause-circle-line"></i>
                            )}
                            Suspendre
                          </button>
                        ) : (
                          <button
                            onClick={() => requestAction(u, 'active')}
                            disabled={actionLoadingId === u.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-secondary-700 border border-secondary-200 hover:bg-secondary-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoadingId === u.id ? (
                              <i className="ri-loader-4-line animate-spin"></i>
                            ) : (
                              <i className="ri-play-circle-line"></i>
                            )}
                            Réactiver
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-[70] max-w-sm">
          <div
            className={`flex items-start gap-3 px-4 py-3 rounded-lg border shadow-xl bg-background-50 ${
              toast.type === 'success'
                ? 'border-secondary-300'
                : 'border-red-300'
            }`}
          >
            <i
              className={`text-lg mt-0.5 ${
                toast.type === 'success'
                  ? 'ri-checkbox-circle-line text-secondary-600'
                  : 'ri-error-warning-line text-red-600'
              }`}
            ></i>
            <p className="text-sm text-foreground-800">{toast.message}</p>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      <ConfirmModal
        open={confirm !== null}
        tone={confirm?.status === 'suspended' ? 'danger' : 'success'}
        title={
          confirm?.status === 'suspended'
            ? 'Suspendre ce compte'
            : 'Réactiver ce compte'
        }
        message={
          confirm
            ? `${
                confirm.status === 'suspended'
                  ? 'Le compte sera suspendu et l\u2019accès aux analyses sera bloqué pour'
                  : 'L\u2019abonnement sera réactivé avec une nouvelle période de 30 jours pour'
              } ${displayName(confirm.user) || confirm.user.email || 'cet utilisateur'}.`
            : ''
        }
        confirmLabel={confirm?.status === 'suspended' ? 'Suspendre' : 'Réactiver'}
        loading={actionLoadingId !== null}
        onConfirm={confirmAction}
        onCancel={() => {
          if (actionLoadingId === null) setConfirm(null);
        }}
      />
    </div>
  );
}